import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";

import { Box, IconButton, alpha } from "@material-ui/core";
import {
  ChatContainer,
  MainContainer,
  Message,
  MessageInput,
  MessageList,
  MessageModel,
} from "@chatscope/chat-ui-kit-react";
import React, { useEffect, useRef, useState } from "react";

import AMLChatMessage from "./amlChatMessage";
import { ArrowUpwardRounded } from "@material-ui/icons";
import { ValidationResult } from "~/pages/singleChannel/amc/customAmcReport";
import { get } from "lodash";
import styled from "styled-components";
import { useGetChatResponseMutation } from "~/store/mystore/amc.redux";
import { useTranslation } from "react-i18next";
import { useTypedSelector } from "~/hooks/useTypedSelector";

const CustomMainContainer = styled(MainContainer)`
  height: 100%;
  border-width: 0 !important;

  .cs-chat-container {
    border: 0 !important;
    background-color: ${({ theme }) => theme.palette.background.default};
  }

  .cs-chat-container .cs-message-input {
    height: 100px;
    padding: 0;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => theme.palette.border.main};
    background-color: ${({ theme }) => theme.palette.background.paper};
  }

  .cs-message-input__tools {
    width: 0;
  }

  .cs-message-input__content-editor-wrapper {
    width: 100%;
    background-color: ${({ theme }) => theme.palette.background.paper};
  }

  .cs-message-input__content-editor {
    font-family: ${({ theme }) => theme.typography.fontFamily};
    color: ${({ theme }) => theme.palette.text.primary};
    background-color: ${({ theme }) => theme.palette.background.paper};
  }

  .cs-message-input__content-editor[data-placeholder]:empty:before {
    color: ${({ theme }) => alpha(theme.palette.text.primary, 0.75)};
  }

  .cs-button--attachment {
    z-index: 100;
  }

  .cs-button--send {
    margin-left: -50px !important;
  }

  .cs-message-list {
    padding: 1rem;
    background-color: ${({ theme }) =>
      theme.darkModeEnabled ? theme.palette.background.paper : "#f0f1f3"};
    border-radius: 10px;
    margin-bottom: 1rem;
    max-height: 500px;
  }

  .cs-message__content {
    background-color: #eaf2fc !important; // Set colour independent of theme
    color: #000f36 !important; // Set colour independent of theme
    padding: 1rem;
    border-radius: 1rem;
    margin: 0.5rem;
    box-shadow: 0px 2px 6px 0px rgba(41, 35, 35, 0.05);
    font-size: ${({ theme }) => theme.typography.h6.fontSize};
    font-family: ${({ theme }) => theme.typography.fontFamily};
  }
`;

const SendBtnWrapper = styled(Box)`
  z-index: 100;
  position: absolute;
  right: 10px;
  bottom: 10px;
`;

const StyledIconButton = styled(IconButton)`
  width: 40px;
  height: 40px;
  background-color: ${({ theme }) => theme.palette.secondary.main};
  border-radius: 50%;
  &:hover {
    background-color: ${({ theme }) => theme.palette.secondary.dark};
  }
`;

const StyledArrowUpwardRounded = styled(ArrowUpwardRounded)`
  fill: ${({ theme }) => theme.palette.secondary.contrastText};
`;

type AMCChatMessage =
  | ({
      messageType: "text";
    } & MessageModel)
  | {
      messageType: "code";
      message: string;
      query: string;
      isValid?: boolean;
      errorMessage?: string;
    };

// TO_DO: remove this default message
const SAMPLE_INITIAL_MESSAGE =
  "Give me a query to show ROAS by campaign and ASIN for Sponsored Products in the last 30 days";

interface AMCChatProps {
  setCode: (code: string) => void;
  validationResult: ValidationResult;
  setValidationResult: (result: ValidationResult) => void;
}

const AMCChat: React.FC<AMCChatProps> = ({
  setCode,
  validationResult,
  setValidationResult,
}) => {
  const { t } = useTranslation();
  const user = useTypedSelector((state) => state.user);
  const store = useTypedSelector((state) =>
    get(state, "persistentAppSettings.setting.data.currentStore")
  );
  const [messages, setMessages] = useState<AMCChatMessage[]>([]);

  const [getChatResponse] = useGetChatResponseMutation();

  useEffect(() => {
    handleSendMessage(SAMPLE_INITIAL_MESSAGE);
  }, []);

  const handleSendMessage = async (inputMessage?: string) => {
    const inputElement = document.querySelector(
      ".cs-message-input__content-editor"
    );

    const message = (inputMessage ?? inputElement?.textContent)?.replace(
      /<[^>]*>/g,
      ""
    );
    if (!inputElement || !message) return;

    setMessages((prev) => [
      ...prev,
      {
        messageType: "text",
        message,
        position: "single",
        direction: "outgoing",
        sentTime: "just now",
        sender: user?.email,
      },
    ]);

    if (validationResult.status) {
      setValidationResult({
        status: null,
        errorMessage: null,
      });
    }

    inputElement.innerHTML = "";
    const response = await getChatResponse({
      mid: store?.merchantId || "",
      prompt: message,
    }).unwrap();

    // regular chat response
    if (response.instanceData?.chatResponse) {
      setMessages((prev) => [
        ...prev,
        {
          messageType: "text",
          message: response.instanceData.chatResponse,
          position: "single",
          direction: "incoming",
          sentTime: "just now",
          sender: "AMC",
        },
      ]);
    } else if (response.instanceData?.sql) {
      // sql chat response
      setMessages((prev) => [
        ...prev,
        {
          messageType: "code",
          message: response.instanceData.name ?? "",
          query: response.instanceData.sql ?? "",
          isValid: response.instanceData.isValid,
          errorMessage: response.instanceData.errorMessage || "",
        },
      ]);
    }
  };

  const getMessageComponent = (chatMessage: AMCChatMessage, index: number) => {
    if (chatMessage.messageType === "text") {
      return <Message model={chatMessage} key={index} />;
    } else {
      return (
        <AMLChatMessage
          message={chatMessage.message}
          query={chatMessage.query}
          key={index}
          setCode={setCode}
          isValid={chatMessage.isValid}
          errorMessage={chatMessage.errorMessage}
          setValidationResult={setValidationResult}
        />
      );
    }
  };

  return (
    <CustomMainContainer>
      <ChatContainer>
        <MessageList>
          {messages.map((message, index) =>
            getMessageComponent(message, index)
          )}
        </MessageList>
        <MessageInput
          placeholder={t("amc.chat.placeholder")}
          attachButton={false}
          sendButton={false}
          onSend={handleSendMessage}
        />
      </ChatContainer>
      <SendBtnWrapper>
        <StyledIconButton onClick={() => handleSendMessage()}>
          <StyledArrowUpwardRounded />
        </StyledIconButton>
      </SendBtnWrapper>
    </CustomMainContainer>
  );
};

export default AMCChat;
